import apiController from "@/utils/apiController";
import translateDbField from "@/utils/translateDbField";

/** Unidades associadas à linha; a primeira serve de escopo para o contrato de subsídio. */
export function unidadesDaLinha(linha) {
  const l = linha || {};
  const ids = new Set();
  const add = (raw) => {
    if (raw == null || raw === "") return;
    const n = Number(typeof raw === "object" ? raw.id_unidade : raw);
    if (Number.isFinite(n) && n > 0) ids.add(n);
  };
  add(l.id_unidade);
  if (Array.isArray(l.unidades)) l.unidades.forEach(add);
  else if (l.unidades != null) String(l.unidades).split(",").forEach((x) => add(x.trim()));
  if (l.ids_unidades != null) {
    String(l.ids_unidades).split(",").forEach((x) => add(x.trim()));
  }
  return Array.from(ids);
}

/**
 * Mantém só os tipos de veículo que têm ao menos uma das tarifas preenchida.
 * Um tipo com tudo zerado não serve de base de custo: comparar R$ 0,00 com R$ 0,00
 * não diz nada, então ele é descartado para que a busca caia na fonte seguinte.
 */
export function tiposVeiculoComTarifa(rows, campos) {
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    if (!row) return false;
    return campos.some((campo) => {
      const n = Number(row[campo]);
      return Number.isFinite(n) && n > 0;
    });
  });
}

/**
 * Escolhe o tipo de veículo cuja lotação melhor atende a linha: o menor que comporte
 * a capacidade mínima; se nenhum comportar (ou não houver alvo), o de maior capacidade.
 * Espelha a ideia da Roteirização, onde o solver escolhe pela capacidade.
 */
export function escolheTipoVeiculoPorLotacao(rows, alvo, campoLotacao) {
  const list = (Array.isArray(rows) ? rows : []).filter(Boolean);
  if (!list.length) return null;
  const cap = (row) => {
    const n = Number(row[campoLotacao]);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };
  const ordenados = [...list].sort((a, b) => cap(a) - cap(b));
  if (Number.isFinite(alvo) && alvo > 0) {
    const cabe = ordenados.find((row) => cap(row) >= alvo);
    if (cabe) return cabe;
  }
  return ordenados[ordenados.length - 1];
}

function descricaoTipo(row) {
  return (
    translateDbField(row.translation_key, row.descricao_tipo_veiculo) ||
    (row.descricao_tipo_veiculo != null ? String(row.descricao_tipo_veiculo) : null)
  );
}

/**
 * Tarifas da estimativa de custo de uma linha: contrato de subsídio do cliente da linha
 * quando houver; senão as tarifas de simulação do cadastro de tipos de veículo — mesma
 * precedência (contrato → padrão) que a tela de Roteirização usa.
 * Retorna os parâmetros de computeRouteCost (+ fonte/tipo/capacidade) ou null.
 */
export async function carregaTarifasCustoLinha(linha, capacidadeMinima) {
  const l = linha || {};
  const idEmpresa = Number(l.id_empresa);
  const idCliente = Number(l.id_cliente);
  const idUnidade = unidadesDaLinha(l)[0];
  const alvo = capacidadeMinima;

  if (Number.isFinite(idEmpresa) && Number.isFinite(idCliente) && Number.isFinite(idUnidade)) {
    try {
      const res = await apiController.request(
        apiController.endpoints.subsidioGetallTiposveiculosfromcontratossubsidio,
        {
          query: { id_empresa: idEmpresa, id_cliente: idCliente, id_unidade: idUnidade },
          silent: true,
        }
      );
      const rows = tiposVeiculoComTarifa(res.data, ["valor_fixo", "valor_variavel", "valor_labor"]);
      const row = escolheTipoVeiculoPorLotacao(rows, alvo, "lotacao");
      if (row) {
        return {
          fonte: "contrato",
          tipo: descricaoTipo(row),
          capacidade: Number(row.lotacao) || null,
          capacidadeMinima: alvo,
          fixedCost: Number(row.valor_fixo) || 0,
          costPerKm: Number(row.valor_variavel) || 0,
          laborCost: Number(row.valor_labor) || 0,
        };
      }
      // Contrato sem nenhuma tarifa preenchida: cai nos padrões de simulação,
      // senão a comparação sairia inteira zerada.
    } catch (e) {
      // Sem contrato acessível: segue para as tarifas padrão.
    }
  }

  const res = await apiController.request(apiController.endpoints.getallTiposveiculos, {
    silent: true,
  });
  const rows = tiposVeiculoComTarifa(res.data, [
    "tarifa_fixa_simulacao",
    "tarifa_variavel_simulacao",
    "tarifa_mao_de_obra_simulacao",
  ]);
  const row = escolheTipoVeiculoPorLotacao(rows, alvo, "lotacao_padrao");
  if (!row) return null;
  return {
    fonte: "padrao",
    tipo: descricaoTipo(row),
    capacidade: Number(row.lotacao_padrao) || null,
    capacidadeMinima: alvo,
    fixedCost: Number(row.tarifa_fixa_simulacao) || 0,
    costPerKm: Number(row.tarifa_variavel_simulacao) || 0,
    laborCost: Number(row.tarifa_mao_de_obra_simulacao) || 0,
  };
}
